import React, { useEffect, useRef, useState } from 'react';
import CustomGrid from '../CustomGrid';
import IncorrectPSEGridConfig from './IncorrectPSE/gridConfig';
import LateShipmentsGridConfig from './LateShipments/gridConfig';
import { constructShipmentsStatusBar, getShipmentsData, isUpdateValid } from '../../../utils';
import { autorun } from 'mobx';
import { useDynamicRoutingValidationContext } from '../../../context/DynamicRoutingValidationContext';
import ValidationReason from '../../ValidationReason';
import { withStyles } from '@material-ui/core';

const styles = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%'
  }
});

const IncorrectShipmentsProperty = ({
  data,
  classes,
  IBPlanningStore,
  updateShipmentInfo,
  enqueueSnackbar,
  isIncorrectPSE,
  currentStepMessage
}) => {
  const gridApi = useRef(null);
  const shipmentBackupDataRef = useRef({}); // Ref to store the backup data
  const [shipments, setShipments] = useState([]);
  const { shipmentUpdates, addShipmentUpdate } = useDynamicRoutingValidationContext();
  const sectionName = isIncorrectPSE ? 'incorrectPse' : 'lateShipments';
  const GridConfig = isIncorrectPSE ? IncorrectPSEGridConfig : LateShipmentsGridConfig;

  useEffect(() => {
    const dispose = autorun(populateShipmentsData);
    return () => dispose(); // Cleanup autorun on unmount
  }, [data]); // Re-run when `data` reference changes

  const onGridReady = (api) => {
    gridApi.current = api;
  };

  const populateShipmentsData = () => {
    if (data) {
      const shipmentsData = getShipmentsData({
        proIdList: data,
        shipmentsList: IBPlanningStore.shipmentsList
      });
      setShipments(shipmentsData);
    }
  };

  const onCellValueChanged = async (params) => {
    const proIdList = [params.data.proId];
    const { field, headerTooltip } = params.colDef;
    const value = params.value;

    try {
      if (!isUpdateValid({ proIdList, field, value, currentCell: params, enqueueSnackbar })) {
        params.data[field] = params.oldValue;
        refreshColumnCells([field]);
        return;
      }

      await updateShipmentInfo({ proIdList, field, value, label: headerTooltip });

      refreshShipmentsData({ proIdList, field, value });

      // Tracking the changed Pro Ids
      addShipmentUpdate(proIdList, sectionName);
    } catch (error) {
      params.data[field] = params.oldValue;
      refreshColumnCells([field]);
    }
  };

  const onBulkUpdate = async ({ field, newValue }) => {
    const selectedRows = gridApi.current.getSelectedRows();
    const proIdList= selectedRows.map((shipment) => shipment.proId);
    try {
      let currentCell = null;
      if (proIdList.length === 1) {
        currentCell = { oldValue: gridApi.current.getSelectedRows()[0][field] };
      }
      if (
        isUpdateValid({
          proIdList,
          field,
          value: newValue,
          currentCell,
          enqueueSnackbar,
          selectedRows,
          isBulkEdit: true
        })
      ) {
        updateShipmentUI({ proIdList, field, value: newValue });
        await updateShipmentInfo({ proIdList, field, value: newValue });

        // Tracking the changed Pro Ids
        addShipmentUpdate(proIdList, sectionName);
      }
    } catch (error) {
      restoreShipmentData({ proIdList, field });
    }
  };

  const refreshShipmentsData = ({ proIdList, field, value }) => {
    setShipments((prevShipments) =>
      prevShipments.map((shipment) =>
        proIdList.includes(shipment.proId) ? { ...shipment, [field]: value } : shipment
      )
    );
  };

  const updateShipmentUI = ({ proIdList, field, value }) => {
    let backupData = {};

    gridApi.current.getSelectedRows().forEach((shipment) => {
      if (!proIdList.includes(shipment.proId)) return;

      backupData[shipment.proId] = { [field]: shipment[field] };
      shipment[field] = value;
    });
    shipmentBackupDataRef.current = backupData;
    refreshColumnCells([field]);
  };

  const restoreShipmentData = ({ proIdList, field }) => {
    gridApi.current.forEachNode((node) => {
      if (proIdList.includes(node.data.proId)) {
        const backupData = shipmentBackupDataRef.current[node.data.proId];
        if (backupData) node.data[field] = backupData[field];
      }
    });
    refreshColumnCells([field]);
  };

  const refreshColumnCells = (fields) => {
    gridApi.current.refreshCells({ columns: fields, force: true });
  };

  const updatedPros = isIncorrectPSE
    ? shipmentUpdates?.incorrectPse
    : shipmentUpdates?.lateShipments;

  return (
    <div className={classes.container}>
      <ValidationReason message={currentStepMessage} />
      <CustomGrid
        title="Shipments"
        GridConfig={GridConfig}
        data={shipments}
        showSearchBar
        showBulkEdit
        onBulkUpdate={onBulkUpdate}
        onCellValueChanged={onCellValueChanged}
        onGridReadyHandler={onGridReady}
        constructStatusBar={constructShipmentsStatusBar}
        updatedPros={updatedPros}
      />
    </div>
  );
};

export default withStyles(styles)(IncorrectShipmentsProperty);
